import React, { useEffect, useState } from 'react';
import { ChevronRight, KeyRound, LogOut, Search } from 'lucide-react';
import SinoAlertas from '@/components/layout/SinoAlertas';
import { ATALHOS } from '@/app/shell/atalhos';
import AlterarSenhaDialog from '@/components/auth/AlterarSenhaDialog';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { findRouteMeta, formatPathAsTitle } from '@/app/registry/routes';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

type HeaderProps = {
  onOpenCommandPalette?: () => void;
};

const Header: React.FC<HeaderProps> = ({ onOpenCommandPalette }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [alterarSenhaOpen, setAlterarSenhaOpen] = useState(false);
  const routeMeta = findRouteMeta(location.pathname);
  const title = routeMeta?.title || formatPathAsTitle(location.pathname);
  const section = routeMeta?.section || 'Portal de Tributos';

  useEffect(() => {
    document.title = `${title} · Portal de Tributos`;
  }, [title]);

  const handleLogout = () => {
    logout();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!user) return null;

  return (
    <header className="border-b border-[var(--brand-hairline)] bg-[var(--brand-charcoal)] text-white">
      <div className="flex h-[var(--app-topbar-h)] items-center justify-between gap-3 px-3 sm:px-5">
        {/* Trilha de uma linha: o título grande fica só na página, para não aparecer duas vezes (28/10/2026). */}
        <nav aria-label="Você está em" className="flex min-w-0 items-center gap-1.5 pl-12 text-sm lg:pl-0">
          <span className="hidden truncate text-[var(--brand-ink-2)] sm:inline">{section}</span>
          <ChevronRight className="hidden h-3.5 w-3.5 shrink-0 text-[var(--brand-ink-3)] sm:inline" />
          <span className="truncate font-semibold text-white" aria-current="page">{title}</span>
        </nav>

        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={onOpenCommandPalette}
            className="hidden h-10 min-w-[260px] items-center gap-2 rounded-md border border-[var(--brand-hairline)] bg-[var(--brand-charcoal-soft)] px-3 text-left text-sm text-[var(--brand-ink-2)] transition-colors hover:bg-white/10 hover:text-white xl:flex"
          >
            <Search className="h-4 w-4" />
            <span className="truncate">Buscar tela ou empresa</span>
            <kbd className="ml-auto rounded border border-[var(--brand-hairline)] bg-[var(--brand-charcoal-deep)] px-1.5 py-0.5 text-[11px] text-[var(--brand-ink-2)]">
              {ATALHOS.buscar}
            </kbd>
          </button>

          <SinoAlertas />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex h-10 items-center gap-1 rounded-md p-2 text-white hover:bg-white/10 sm:gap-2 sm:px-3">
              <Avatar className="h-6 w-6 sm:h-8 sm:w-8">
                <AvatarFallback className="bg-white text-xs text-[#34343A] sm:text-sm">
                  {getInitials(user.nome)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:inline-block font-medium text-sm">
                {user.nome}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-medium">{user.nome}</span>
                <span className="text-sm text-muted-foreground">{user.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {user.origem === 'local' && (
              <DropdownMenuItem onClick={() => setAlterarSenhaOpen(true)}>
                <KeyRound className="mr-2 h-4 w-4" />
                Alterar senha
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleLogout} className="text-[var(--app-danger)]">
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {user.origem === 'local' && (
          <AlterarSenhaDialog email={user.email} open={alterarSenhaOpen} onOpenChange={setAlterarSenhaOpen} />
        )}
        </div>
      </div>
    </header>
  );
};

export default Header;
